import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { JsonHalCollection, JsonHalCollectionPageInfo } from 'src/app/models/api/jsonhal.model';
import {
  DataLayerItemType,
  DataSourceType,
  DataLensItemType,
  DataLensSourceType,
  IDataItemAccessAnalytics,
  IDataItemTopAnalytics,
  IDataLayerItem,
  DataAccessStatus,
} from 'src/app/models/api/dataLayer.model';
import { DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';

export interface IFetchContentInfo {
  readonly stepId: string;
  readonly integrationId: string;
}

/**
 * API response format for data lens items
 */
interface IDataLensItemApiResponse {
  readonly _links: {
    self: {
      href: string;
    };
  };
  readonly id: string;
  readonly organizationId: string;
  readonly name: string;
  readonly fileExtension: string | null;
  readonly type: DataLensItemType;
  readonly source: DataLensSourceType;
  readonly counter: number;
  readonly lastAccessAt: string;
  readonly createdAt: string;
  readonly sensitivityLabel: string | null;
  readonly findingsBySeverity?: Record<DetectionRuleSeverity, number>;
  readonly url?: string | null;
  readonly fetchContentInfo?: IFetchContentInfo | null;
}

/**
 * API response format for access analytics endpoint
 * Note: API returns snake_case field names
 */
interface IAccessAnalyticsApiResponse {
  readonly data_item_id: string;
  readonly platform_access_by_date: {
    readonly date: string;
    readonly service: string;
    readonly count: number;
  }[];
  readonly total_access_count: number;
  readonly date_range_start: string;
  readonly date_range_end: string;
}

/**
 * API response format for top analytics (new split endpoint)
 * Note: API returns snake_case field names
 */
interface ITopAnalyticsApiResponse {
  readonly data_item_id: string;
  readonly top_agents: {
    readonly agent_id: string;
    readonly agent_name: string;
    readonly count: number;
  }[];
  readonly other_agents_count: number;
  readonly top_users: {
    readonly user_id: string;
    readonly user_display_name: string;
    readonly count: number;
  }[];
  readonly other_users_count: number;
}
import { Platform } from 'src/app/models/api/common';
import { LoggerService } from '../../infra/logger/logger.service';
import { HttpService } from '../http/http.service';

@Injectable({
  providedIn: 'root',
})
export class DataLayerHttpService {
  public readonly defaultPageSize: number = 50;
  private readonly _baseUrl = 'detection/data-lens';

  constructor(
    private readonly _httpService: HttpService,
    private readonly _logger: LoggerService
  ) {}

  /**
   * Get available sensitivity labels for the account
   */
  public getSensitivityLabels(): Observable<string[]> {
    return this._httpService.get<string[]>('detection/data-lens/sensitivity-labels', undefined, undefined, true).pipe(
      catchError((error) => {
        this._logger.error('DataLayerHttpService: Error fetching sensitivity labels', {}, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Data Layer items from the API
   */
  public getDataLayerItems(
    filterOptions: ISelectedFilters,
    sortData?: SortColumn
  ): Observable<JsonHalCollection<IDataLayerItem>> {
    const params = this._buildQueryParams(filterOptions, sortData);

    return this._httpService
      .get<JsonHalCollection<IDataLensItemApiResponse>>('detection/data-lens/items', undefined, params, true)
      .pipe(
        map((response) => this._mapApiResponseToViewModel(response)),
        catchError((error) => {
          this._logger.error('DataLayerHttpService: Error fetching data layer items', {}, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get Data Layer item by continuation token (for pagination)
   */
  public getDataLayerItemsByContinuationToken(
    continuationToken: string
  ): Observable<JsonHalCollection<IDataLayerItem>> {
    const params = new HttpParams().set('continuationToken', continuationToken);

    return this._httpService
      .get<JsonHalCollection<IDataLensItemApiResponse>>('detection/data-lens/items', undefined, params, true)
      .pipe(
        map((response) => this._mapApiResponseToViewModel(response)),
        catchError((error) => {
          this._logger.error('DataLayerHttpService: Error fetching data layer items by continuation token', {}, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get single data layer item by ID
   */
  public getDataLayerItem(itemId: string): Observable<IDataLayerItem> {
    return this._httpService
      .get<IDataLensItemApiResponse>(`${this._baseUrl}/items/${itemId}`, undefined, undefined, true)
      .pipe(
        map((item) => this._mapApiItemToDataLayerItem(item)),
        catchError((error) => {
          this._logger.error(`Failed to fetch data layer item ${itemId}`, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get access time series analytics for a specific data item
   * Uses the new /analytics/access endpoint
   */
  public getDataItemAccessAnalytics(
    dataItemId: string,
    startDate: Date,
    endDate: Date
  ): Observable<IDataItemAccessAnalytics> {
    const params = new HttpParams().set('startDate', startDate.toISOString()).set('endDate', endDate.toISOString());

    return this._httpService
      .get<IAccessAnalyticsApiResponse>(
        `${this._baseUrl}/items/${dataItemId}/analytics/access`,
        undefined,
        params,
        true
      )
      .pipe(
        map((response) => this._mapAccessAnalyticsResponse(response)),
        catchError((error) => {
          this._logger.error(`Failed to fetch access analytics for ${dataItemId}`, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get top agents/users analytics for a specific data item
   * Uses the new /analytics/top endpoint (fixed 90-day lookback, no date params)
   */
  public getDataItemTopAnalytics(dataItemId: string): Observable<IDataItemTopAnalytics> {
    return this._httpService
      .get<ITopAnalyticsApiResponse>(`${this._baseUrl}/items/${dataItemId}/analytics/top`, undefined, undefined, true)
      .pipe(
        map((response) => this._mapTopAnalyticsResponse(response)),
        catchError((error) => {
          this._logger.error(`Failed to fetch top analytics for ${dataItemId}`, error);
          return throwError(() => error);
        })
      );
  }

  public getDisplayNameById(filterName: string, filterIds: string[]): Observable<AutocompleteSearchResultsV2Model[]> {
    const url = 'detection/autocomplete/agentSteps/options';
    const params = new HttpParams()
      .set('pageSize', this.defaultPageSize.toString())
      .set('filterName', filterName)
      .set('filterId', filterIds.join(','));

    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, params, true).pipe(
      catchError((error) => {
        this._logger.error('DataLayerHttpService: Error fetching display names', {}, error);
        return throwError(() => error);
      })
    );
  }

  public getAutocompleteOptions(query: string, filterName: string): Observable<AutocompleteSearchResultsV2Model[]> {
    const supportedFilterNames = ['actor', 'agent'];
    const mappedFilterName: Record<string, string> = {
      actor: 'mainActorDisplayName',
      agent: 'aiAgentDisplayName',
    };

    if (!supportedFilterNames.includes(filterName)) {
      return of([]);
    }

    const url = 'detection/autocomplete/agentSteps';
    const mapped = mappedFilterName[filterName];
    const params = new HttpParams()
      .set('pageSize', this.defaultPageSize.toString())
      .set('query', query)
      .set('filterName', mapped);

    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, params, true).pipe(
      catchError((error) => {
        this._logger.error('DataLayerHttpService: Error fetching autocomplete options', {}, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Build query parameters for the data lens API endpoint
   */
  private _buildQueryParams(filterOptions: ISelectedFilters, sortData?: SortColumn, searchAfter?: string): HttpParams {
    let params = new HttpParams().set('pageSize', (this.defaultPageSize || 50).toString());

    // Add search_after token if provided
    if (searchAfter) {
      params = params.set('search_after', searchAfter);
    }

    // Add sorting (must be in array format: [field(direction)])
    // Brackets will be URL-encoded to %5B and %5D
    if (sortData?.column?.sortKey) {
      const sortField = this._mapSortKeyToApiField(sortData.column.sortKey);
      const sortDirection = sortData.isDesc ? 'desc' : 'asc';
      params = params.set('sortBy', `[${sortField}(${sortDirection})]`);
    }

    if (filterOptions.search) {
      params = params.append('name', filterOptions.search);
    }

    if (filterOptions.fromDate) {
      params = params.append('sinceTimestamp', filterOptions.fromDate.toISOString());
    }

    if (filterOptions.toDate) {
      params = params.append('untilTimestamp', filterOptions.toDate.toISOString());
    }

    if (filterOptions.dataType && filterOptions.dataType.length > 0) {
      filterOptions.dataType.forEach((type: string) => {
        params = params.append('type', type);
      });
    }

    if (filterOptions.fileExtension && filterOptions.fileExtension.length > 0) {
      filterOptions.fileExtension.forEach((ext: string) => {
        params = params.append('fileExt', ext);
      });
    }

    if (filterOptions.sensitivityLabel && filterOptions.sensitivityLabel.length > 0) {
      filterOptions.sensitivityLabel.forEach((label: string) => {
        params = params.append('sensitivityLabel', label);
      });
    }

    if (filterOptions.service && filterOptions.service.length > 0) {
      params = params.append('aiService', filterOptions.service.join(','));
    }

    if (filterOptions.actor && filterOptions.actor.length > 0) {
      params = params.append('mainActorId', filterOptions.actor.join(','));
    }

    if (filterOptions.agent && filterOptions.agent.length > 0) {
      params = params.append('aiAgentId', filterOptions.agent.join(','));
    }

    return params;
  }

  /**
   * Map sort key from table column to API field name
   */
  private _mapSortKeyToApiField(sortKey: string): string {
    const sortKeyMap: Record<string, string> = {
      resourceName: 'name',
      accessCount: 'counter',
      lastAccessed: 'last_access_at',
    };
    return sortKeyMap[sortKey] || 'name';
  }

  /**
   * Map API response with HAL format to view model format
   * Similar to how other services handle responses
   */
  private _mapApiResponseToViewModel(
    response: JsonHalCollection<IDataLensItemApiResponse>
  ): JsonHalCollection<IDataLayerItem> {
    // Map API items to view model items
    const mappedItems = response._embedded.items.map((item) => this._mapApiItemToDataLayerItem(item));

    // Extract page info from response
    const responsePage: JsonHalCollectionPageInfo = response.page;

    // Return HAL collection with mapped items, preserving pagination metadata
    return {
      _embedded: {
        items: mappedItems,
      },
      _links: response._links,
      page: {
        number: responsePage.number ?? 1, // Current page number (1-indexed: page 1 is first page)
        size: responsePage.size || this.defaultPageSize,
        totalItems: responsePage.totalItems || 0,
        totalPages: responsePage.totalPages || 1,
      },
    };
  }

  /**
   * Map API item to IDataLayerItem format
   */
  private _mapApiItemToDataLayerItem(apiItem: IDataLensItemApiResponse): IDataLayerItem {
    return {
      id: apiItem.id,
      metadata: {
        organizationId: apiItem.organizationId,
        api_type: apiItem.type,
        api_source: apiItem.source,
      },
      resourceName: apiItem.name,
      dataType: this._mapApiTypeToDataLayerType(apiItem.type),
      sourceType: this._mapApiSourceToDataSourceType(apiItem.source),
      platform: Platform.powerplatform, // TODO: Determine platform from API response if available
      fileExtension: apiItem.fileExtension,
      lastAccessed: new Date(apiItem.lastAccessAt),
      firstAccessed: new Date(apiItem.createdAt),
      accessCount: apiItem.counter,
      sensitivityLabel: apiItem.sensitivityLabel,
      status: DataAccessStatus.active, // TODO: Determine status from API response if available
      tags: apiItem.fileExtension ? [apiItem.fileExtension] : [],
      file_extension: apiItem.fileExtension,
      counter: apiItem.counter,
      last_access_at: new Date(apiItem.lastAccessAt),
      created: new Date(apiItem.createdAt),
      sensitivity_label: apiItem.sensitivityLabel,
      resourceId: apiItem.id,
      url: apiItem.url,
      findingsBySeverity: apiItem.findingsBySeverity,
      fetchContentInfo: apiItem.fetchContentInfo,
      _links: apiItem._links as { self: { href: string }; next: { href: string } },
    };
  }

  /**
   * Map API type to DataLayerItemType enum
   */
  private _mapApiTypeToDataLayerType(apiType: DataLensItemType): DataLayerItemType {
    const typeMap: Record<DataLensItemType, DataLayerItemType> = {
      file: DataLayerItemType.file,
      website: DataLayerItemType.website,
      email: DataLayerItemType.email,
      messages: DataLayerItemType.unknown,
      unknown: DataLayerItemType.unknown,
    };
    return typeMap[apiType] || DataLayerItemType.unknown;
  }

  /**
   * Map API source to DataSourceType enum
   */
  private _mapApiSourceToDataSourceType(apiSource: DataLensSourceType): DataSourceType {
    const sourceMap: Record<DataLensSourceType, DataSourceType> = {
      cloud_share: DataSourceType.sharePoint,
      website: DataSourceType.other,
      unknown: DataSourceType.other,
    };
    return sourceMap[apiSource] || DataSourceType.other;
  }

  /**
   * Map access analytics API response to IDataItemAccessAnalytics model
   * Converts snake_case API response to camelCase model
   */
  private _mapAccessAnalyticsResponse(response: IAccessAnalyticsApiResponse): IDataItemAccessAnalytics {
    return {
      dataItemId: response.data_item_id || '',
      platformAccessByDate: (response.platform_access_by_date || []).map((item) => ({
        date: item.date,
        service: item.service,
        count: item.count,
      })),
      totalAccessCount: response.total_access_count || 0,
      dateRangeStart: response.date_range_start || '',
      dateRangeEnd: response.date_range_end || '',
    };
  }

  /**
   * Map top analytics API response to IDataItemTopAnalytics model
   * Converts snake_case API response to camelCase model
   */
  private _mapTopAnalyticsResponse(response: ITopAnalyticsApiResponse): IDataItemTopAnalytics {
    return {
      dataItemId: response.data_item_id || '',
      topAgents: (response.top_agents || []).map((agent) => ({
        agentId: agent.agent_id,
        agentName: agent.agent_name,
        count: agent.count,
      })),
      otherAgentsCount: response.other_agents_count || 0,
      topUsers: (response.top_users || []).map((user) => ({
        userId: user.user_id,
        userDisplayName: user.user_display_name,
        count: user.count,
      })),
      otherUsersCount: response.other_users_count || 0,
    };
  }
}
